/**
 * SLARow — a single selectable delivery option.
 *
 * Purely presentational: it receives a mapped `SlaOption` and selection state,
 * and reports clicks upward. All copy/derivation happens in the mapping layer.
 */
import React, { FC, KeyboardEvent } from 'react';
import classNames from 'classnames/bind';
import Text from '../../atoms/Text';
import properties from '../../../modules/ajio-util/properties';
import DeliveryMethodChip from './DeliveryMethodChip';
import SLABadge from './SLABadge';
import { SlaOption } from './types';
import styles from './index.pcss';

const cx = classNames.bind(styles);
const imageBaseUrl = properties.getProperty('imageBaseUrl');
const ajiogramTick = `${imageBaseUrl}/static/img/ajiogram-tick.svg`;
const ajiogramTickSelected = `${imageBaseUrl}/static/img/ajiogram-tick-selected.svg`;

const RadioIndicator: FC<{ selected: boolean; disabled: boolean; fleek?: boolean }> = ({
  selected,
  disabled,
  fleek = false
}) => {
  // Ajiogram uses dedicated tick assets instead of the CSS radio circle.
  if (fleek) {
    return (
      <img
        className={cx('sla-row__radio-img')}
        src={selected ? ajiogramTickSelected : ajiogramTick}
        alt=""
        aria-hidden="true"
      />
    );
  }
  return (
    <span
      className={cx('sla-row__radio', {
        'sla-row__radio--selected': selected,
        'sla-row__radio--disabled': disabled
      })}
      aria-hidden="true"
    >
      {selected && (
        <svg width="12" height="12" viewBox="0 0 24 24" focusable="false">
          <path
            fill="#FFFFFF"
            d="M9.55 17.3 4.4 12.15l1.4-1.4 3.75 3.75 8.25-8.25 1.4 1.4z"
          />
        </svg>
      )}
    </span>
  );
};

interface SLARowProps {
  option: SlaOption;
  selected: boolean;
  /** Whether to render the top-right badge (multi-SLA only). */
  showBadge: boolean;
  onSelect: (option: SlaOption) => void;
  /** Ajiogram (fleek) theme. */
  fleek?: boolean;
  /** Cart only — suffix the amount with "/ pc" (prices there are per piece). */
  perPiece?: boolean;
}

const SLARow: FC<SLARowProps> = ({ option, selected, showBadge, onSelect, fleek = false, luxe = false, perPiece = false }) => {
  const disabled = !option.serviceable;
  const primaryColor = fleek ? 'White' : luxe ? 'Light_Black' : 'Nero';
  const discountColor = fleek ? 'Fleek_Green' : luxe ? 'Light_Black' : 'Jade';

  const handleSelect = (): void => {
    if (disabled) return;
    onSelect(option);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (disabled) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onSelect(option);
    }
  };

  const ariaLabel = [
    `${option.timelineLabel} ${option.timelineValue}`,
    option.orderByText,
    option.price.mrp ? option.price.price : `MRP ${option.price.price}`,
    option.price.discount,
    option.price.mrp ? `MRP ${option.price.mrp}` : '',
    showBadge ? option.badgeLabel : '',
    disabled ? 'not deliverable' : ''
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <div
      className={cx('sla-row', {
        'sla-row--selected': selected && !disabled,
        'sla-row--disabled': disabled
      })}
      role="radio"
      aria-checked={selected && !disabled}
      aria-disabled={disabled}
      aria-label={ariaLabel}
      tabIndex={disabled ? -1 : 0}
      onClick={handleSelect}
      onKeyDown={handleKeyDown}
    >
      <RadioIndicator selected={selected && !disabled} disabled={disabled} fleek={fleek} />

      <div className={cx('sla-row__body')} aria-hidden="true">
        <Text variant="T2" color={primaryColor} removeGrouping>
          {option.timelineLabel}
        </Text>
        <div className={cx('sla-row__timeline')}>
          <Text variant="T3" weight="bold" color={primaryColor} removeGrouping>
            {option.timelineValue}
          </Text>
          <DeliveryMethodChip method={option.deliveryMethod} fleek={fleek} />
        </div>
        {disabled ? (
          <Text variant="T1" color="Salmon" removeGrouping>
            Not deliverable to this location
          </Text>
        ) : (
          option.orderByText && (
            <Text variant="T1" color={fleek ? 'White' : 'Dark_Grey'} removeGrouping>
              {option.orderByText}
            </Text>
          )
        )}
      </div>

      <div className={cx('sla-row__pricing')} aria-hidden="true">
        {showBadge && <SLABadge label={option.badgeLabel} fleek={fleek} />}
        <div className={cx('sla-row__price')}>
          {/* No markdown to show, so the price itself is the MRP — label it. */}
          {!option.price.mrp && (
            <Text variant="T2" weight="bold" color={primaryColor} removeGrouping>
              MRP
            </Text>
          )}
          <Text variant="T2" weight="bold" color={primaryColor} removeGrouping>
            {option.price.price}
          </Text>
          {option.price.discount && (
            <Text variant="T2" weight="bold" color={discountColor} removeGrouping>
              {option.price.discount}
            </Text>
          )}
          {/* Qualifies whichever amount is last — the discount when present, else the price. */}
          {perPiece && <span className={cx('sla-row__per-piece')}>/ pc</span>}
        </div>
        {/* Only the amount is struck through — the "MRP" label stays plain. */}
        {option.price.mrp && (
          <Text variant="T2" color={fleek ? 'Grey_White' : 'Dark_Grey'} removeGrouping>
            {'MRP '}
            <span className={cx('sla-row__mrp-strike')}>{option.price.mrp}</span>
          </Text>
        )}
      </div>
    </div>
  );
};

export default SLARow;
